






































import { ChartSet, ChartStyle, CHART_STYLE_OPTIONS, LineChartType } from '@/components/charts/model';
import { AufzugheldenMetricNames } from '@/features/device-roles/aufzughelden-device/constants';
import { MetricDescriptorInput, SpotRole } from '@/types/iot-portal';
import { isDef } from '@/util/lang';
import { ArrayProp, BooleanProp, DateProp, EnumProp, ObjectProp, StringProp } from '@/util/prop-decorators';
import { difference, groupBy, partition } from 'lodash';
import { DateTime } from 'luxon';
import moment from 'moment';
import { AsyncComponent, Component as ComponentInterface } from 'vue';
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { DeviceRoleAggregationMetric } from '../../model';
import { formatMetricName, formatMetricValue, isBooleanMetric } from '../../util/metric-formatters';
import DeviceRoleMapMixin from '../mixins/device-role-map';
import spotQuery from '@/features/core/components/spot-metrics-graph-panel-control/spot.gql';

import { MetricResolutionAggregator, RangeInterval, MetricResolutionInput } from '@/types/hsc-types';

import {
  CoreSpotMetricsGraphPanelControlAggregationQuery_aggregation,
  CoreSpotMetricsGraphPanelControlAggregationQuery_aggregation_first_metrics,
} from '../spot-metrics-graph-panel-control/__generated__/CoreSpotMetricsGraphPanelControlAggregationQuery';
import {
  CoreSpotMetricsGraphPanelControlSpotQuery,
  CoreSpotMetricsGraphPanelControlSpotQueryVariables,
  CoreSpotMetricsGraphPanelControlSpotQuery_spot_first_metrics,
  CoreSpotMetricsGraphPanelControlSpotQuery_spot_first_metrics_ContinuousMetric_points,
  CoreSpotMetricsGraphPanelControlSpotQuery_spot_first_metrics_DiscreteMetric_points,
} from '../spot-metrics-graph-panel-control/__generated__/CoreSpotMetricsGraphPanelControlSpotQuery';
import { normalizeMetricPoint } from '../../util/metrics';
import { PropertySpotQuery } from '@/features/domain-ui/heating-system-metrics-graph/__generated__/PropertySpotQuery';
import { checkMetricName, isMetricVisibleOnChart } from '../../util/metric-calculations';

type ContinuousMetricPoint = CoreSpotMetricsGraphPanelControlSpotQuery_spot_first_metrics_ContinuousMetric_points;
type DiscreteMetricPoint = CoreSpotMetricsGraphPanelControlSpotQuery_spot_first_metrics_DiscreteMetric_points;
type MetricPointUnion = ContinuousMetricPoint | DiscreteMetricPoint;
type PropertySpot = PropertySpotQuery['propertySpot'];

type MergedMetricsUnion =
  | CoreSpotMetricsGraphPanelControlSpotQuery_spot_first_metrics
  | CoreSpotMetricsGraphPanelControlAggregationQuery_aggregation_first_metrics;

@Component({
  apollo: {
    spot: {
      query: spotQuery,
      skip(this: HeatingSystemMetricsGraphPanelControl): boolean {
        return this.sideEffectedMetricNames.length == 0 || !this.spotId;
      },
      variables(this: HeatingSystemMetricsGraphPanelControl): CoreSpotMetricsGraphPanelControlSpotQueryVariables {
        return {
          spotId: this.spotId,
          descriptors: this.sideEffectedMetricNames.map((metricName) => ({
            name: metricName,
            ...this.descriptorDates,
          })),
        };
      },
    },
  },
  data() {
    return {
      spot: undefined,
      chartStyle: undefined,
      LineChartType,
    };
  },
})
export default class HeatingSystemMetricsGraphPanelControl extends Mixins(DeviceRoleMapMixin) {
  @StringProp(true)
  private readonly spotId!: string;

  @ObjectProp(true)
  private readonly propertySpot!: PropertySpot;

  @ArrayProp(() => [])
  private readonly metricNames!: string[];

  @ArrayProp(() => [])
  private readonly availableOnGraphMetrics!: string[];

  @DateProp()
  private readonly startDate?: Date;

  @DateProp()
  private readonly stopDate?: Date;

  @EnumProp(false, ...Object.keys(RangeInterval))
  private readonly aggregationInterval?: RangeInterval;

  @EnumProp(false, ...Object.keys(MetricResolutionAggregator))
  private readonly aggregator?: MetricResolutionAggregator;

  @ObjectProp(() => ({}))
  private readonly aggregation?: CoreSpotMetricsGraphPanelControlAggregationQuery_aggregation;

  @BooleanProp(true)
  private readonly metricsLoading!: boolean;

  private readonly spot?: CoreSpotMetricsGraphPanelControlSpotQuery['spot'];

  private chartStyle!: ChartStyle;
  private LineChartType!: LineChartType;
  private mergedMetrics: MergedMetricsUnion[] = [];
  private get graphWidget(): AsyncComponent | ComponentInterface | undefined {
    if (!this.spot) {
      return undefined;
    }
    const graphWidget = this.deviceRoleMap[this.spot.first.role].graphWidget;

    if (graphWidget === undefined || !graphWidget.enabled) {
      return undefined;
    }

    return graphWidget.component;
  }

  private get influxResolutionInput(): string {
    if (this.aggregationInterval) {
      switch (this.aggregationInterval) {
        case RangeInterval.DAY:
          return 'P1D';
        case RangeInterval.MONTH:
          return 'P1M';
        case RangeInterval.WEEK:
          return 'P1W';
        case RangeInterval.HOUR:
          return 'PT1H';
        default:
          return 'PT1M';
      }
    } else {
      return 'PT1H';
    }
  }

  private get graphWidgetProps(): Record<string, unknown> | undefined {
    if (!this.spot) {
      return undefined;
    }

    return this.deviceRoleMap[this.spot.first.role].graphWidget?.props ?? {};
  }

  private get descriptorDates(): Pick<MetricDescriptorInput, 'start' | 'stop'> {
    const timezoneOffset = moment(this.startDate).utcOffset();

    const start =
      this.startDate === undefined
        ? undefined
        : moment(this.startDate).utc().add(timezoneOffset, 'minutes').startOf('day').toDate();
    const stop = this.stopDate === undefined ? undefined : moment(this.stopDate).add(1, 'day').startOf('day').toDate();

    return { start, stop };
  }

  private get aggregationMetricSet(): Set<string> {
    return new Set(
      this.deviceRoleMap[this.spot?.first.role ?? '']?.aggregationMetricNames?.map(({ name }) => name) ?? [],
    );
  }

  private get aggregationMetricMap(): Map<string, DeviceRoleAggregationMetric> {
    return new Map(
      this.deviceRoleMap[this.spot?.first.role ?? '']?.aggregationMetricNames?.map((metric) => [metric.name, metric]),
    );
  }

  private get aggregationResolutionMap(): Map<string, MetricResolutionInput | undefined> {
    return new Map(
      Array.from(this.aggregationMetricMap.entries())
        .filter((entry) => (this.aggregator ? entry[1].aggregators.includes(this.aggregator) : true))
        .map(([aggregationMetricName, metric]: [string, DeviceRoleAggregationMetric]) => {
          if (metric === undefined) {
            return [aggregationMetricName, undefined];
          }

          return [
            aggregationMetricName,
            {
              interval: this.aggregationInterval ?? RangeInterval.HOUR,
              aggregator: this.aggregator ?? MetricResolutionAggregator.MEAN,
            },
          ];
        }),
    );
  }
  private get metricData(): ChartSet[] {
    const aggregatedMetrics = (this.aggregation?.first?.metrics ?? []).filter(({ name }) =>
      isMetricVisibleOnChart(name),
    );

    if (aggregatedMetrics.length === 0) {
      return [];
    }

    const sets = aggregatedMetrics.map((metric) => {
      const points = this.getGdprMetricPoints(metric.points)
        .map(normalizeMetricPoint)
        .map(({ time, value }) => {
          let x = time;
          if (
            metric.name === 'outsideTemperature' &&
            this.aggregationInterval !== RangeInterval.HOUR &&
            this.influxResolutionInput !== 'PT1H'
          ) {
            if (typeof time === 'string') {
              const dateObject = DateTime.fromISO(time, { zone: 'utc' }).set({
                hour: 0,
                minute: 0,
                second: 0,
              });
              x = dateObject.toISO();
            }
          }
          return {
            x,
            y: Number(value).toFixed(2),
          };
        });
      const metricLabel = this.metricLabel(metric);
      return {
        label: metricLabel,
        name: metric.name,
        color: metric.name === 'outsideTemperature' ? '#3b3939' : this.getColorCode(metricLabel),
        points,
      };
    });

    return sets;
  }

  private getColorCode(metricName: string): string {
    return /rücklauf|auslasstemperatur|zirkulation|oben/gi.test(metricName) ? '#0000FF' : '#FF0000';
  }
  private metricLabel(metric: MergedMetricsUnion): string {
    const label = this.spot?.first.metricConfigurations.find(({ name }) => name === metric.name)?.label;
    if (label) {
      return label;
    }
    if (this.aggregation?.first?.role === SpotRole.OUTSIDE_TEMPERATURE && metric.name === 'temperature') {
      return 'Außentemperatur';
    }
    return formatMetricName(metric.name);
  }

  private yAxisLabelFormatter(value: string | number): string {
    if (this.chartStyle === ChartStyle.STEP && this.chartMetricNames.some(isBooleanMetric)) {
      return value === '0' ? 'no/off' : 'yes/on';
    }
    return this.chartMetricNames.length === 0
      ? `${value}`
      : `${this.formatValue(value, this.chartMetricNames[0]) ?? value}`;
  }

  private formatValue(value: string | number, name: string): string {
    // Any metric with temperature in it will have Celsius unit
    if (name.toLowerCase().includes('temperature')) {
      return formatMetricValue('temperature', value);
    }
    return formatMetricValue(this.chartMetricNames.find((v) => v === name) ?? '', value);
  }

  private chartStyleOptions = CHART_STYLE_OPTIONS;

  @Watch('metricNames')
  private updateSelectedMetrics(): void {
    this.metricNamesSideEffect();
  }

  private get chartMetricNames(): string[] {
    return this.metricNames.filter((metricName) => isMetricVisibleOnChart(metricName));
  }

  // @Watch('aggregationInterval')
  // private onAggregationIntervalUpdate(): void {
  //   if (this.aggregation) {
  //     this.aggregation.first.metrics = [];
  //   }
  // }

  private get sideEffectedMetricNames(): string[] {
    if (this.chartMetricNames.includes(AufzugheldenMetricNames.DoorCycles)) {
      return difference(
        [...this.chartMetricNames, AufzugheldenMetricNames.DoorOpenings, AufzugheldenMetricNames.DoorClosings],
        Array.from(this.aggregationMetricSet),
      );
    }
    return difference(this.chartMetricNames, Array.from(this.aggregationMetricSet));
  }

  private metricNamesSideEffect(): void {
    if (this.chartMetricNames.length !== this.metricNames.length) {
      this.$emit('update:selected-metrics', this.chartMetricNames);
    }

    if (
      this.spot?.first.role === SpotRole.AUFZUGHELDEN_DEVICE &&
      this.chartMetricNames.includes(AufzugheldenMetricNames.DoorCycles)
    ) {
      this.chartStyle = ChartStyle.BAR;
    }

    for (const metricName of this.chartMetricNames) {
      if (isBooleanMetric(metricName)) {
        this.chartStyle = ChartStyle.STEP;
        this.$emit(
          'update:selected-metrics',
          this.chartMetricNames.filter((name) => isBooleanMetric(checkMetricName(name))),
        );
        return;
      }
    }

    this.chartStyle = ChartStyle.LINE;
  }

  private getGdprMetricPoints(metricPoints: MetricPointUnion[]): MetricPointUnion[] {
    if (
      ![SpotRole.HEAT_COST_ALLOCATOR, SpotRole.HEAT_METER_COUNTER, SpotRole.WATER_METER_HOT].includes(
        this.spot?.first.role ?? SpotRole.UNKNOWN,
      )
    ) {
      return metricPoints;
    }
    const now = DateTime.utc();

    const [newerThan2Weeks, olderThan2Weeks] = partition(
      metricPoints,
      ({ time }) => DateTime.fromISO(time.toString()).plus({ weeks: 2 }) >= now,
    );

    const pointsGroupedByYearMonth = groupBy(
      olderThan2Weeks,
      ({ time }) => `${DateTime.fromISO(time.toString()).year}-${DateTime.fromISO(time.toString()).month}`,
    );

    const filteredPointsOlderThan2Weeks = Object.values(pointsGroupedByYearMonth).flatMap((points) =>
      this.filter1thAnd16th(points),
    );

    const gdprPoints = [...newerThan2Weeks, ...filteredPointsOlderThan2Weeks].sort(
      (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
    );

    return gdprPoints;
  }

  private filter1thAnd16th(metricPoints: MetricPointUnion[]): MetricPointUnion[] {
    const firstOfMonth = metricPoints.find(({ time }) => {
      return DateTime.fromISO(time.toString()).hasSame(DateTime.fromISO(time.toString()).startOf('month'), 'day');
    });

    const sixteenthOfMonth = metricPoints.find(({ time }) => {
      const date = DateTime.fromISO(time.toString());

      return DateTime.fromISO(time.toString()).hasSame(DateTime.utc(date.year, date.month, 16), 'day');
    });

    if (firstOfMonth === undefined && sixteenthOfMonth === undefined) {
      return [];
    }

    return [firstOfMonth, sixteenthOfMonth].filter(isDef);
  }

  private get yAxisDimensions(): Array<string | number> {
    if (this.chartStyle === ChartStyle.STEP) {
      return ['0', '1'];
    }

    return [];
  }

  private mounted(): void {
    this.metricNamesSideEffect();
  }
}
